/**
 * components/layout/LegalLayout.tsx
 * Shared layout for all legal/info pages.
 * Includes: navbar, footer, mobile responsive, cookie banner.
 */

import React, { useState, useEffect } from 'react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC',
  surface:'#FAFAF8', white:'#FFFFFF', dark:'#12100E',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Cookie Banner ─────────────────────────────────────────────────────────────
export const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false });

  useEffect(() => {
    const consent = localStorage.getItem('Jötr_cookie_consent');
    if (!consent) setTimeout(() => setVisible(true), 1200);
  }, []);

  const accept = (all: boolean) => {
    const saved = all
      ? { essential: true, analytics: true, marketing: true, ts: Date.now() }
      : { essential: true, analytics: prefs.analytics, marketing: prefs.marketing, ts: Date.now() };
    localStorage.setItem('Jötr_cookie_consent', JSON.stringify(saved));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:9999, padding:'16px', display:'flex', justifyContent:'center' }}>
      <div style={{ background:C.dark, borderRadius:16, padding:'20px 24px', maxWidth:680, width:'100%', boxShadow:'0 -4px 40px rgba(0,0,0,0.25)', border:'1px solid rgba(255,255,255,0.08)' }}>
        {!showDetail ? (
          // Simple view
          <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:14 }}>
            <div style={{ flex:1, minWidth:260 }}>
              <p style={{ fontSize:13, color:'rgba(255,255,255,0.85)', fontFamily:font.body, lineHeight:1.6, margin:0 }}>
                🍪 We use cookies to improve your experience and analyse usage. See our{' '}
                <a href="/cookies" style={{ color:C.amber300, textDecoration:'none', fontWeight:600 }}>Cookie Policy</a>.
              </p>
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0, flexWrap:'wrap' }}>
              <button onClick={()=>setShowDetail(true)} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.7)', fontSize:12, fontFamily:font.body, cursor:'pointer', fontWeight:600 }}>
                Manage
              </button>
              <button onClick={()=>accept(false)} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.7)', fontSize:12, fontFamily:font.body, cursor:'pointer', fontWeight:600 }}>
                Reject all
              </button>
              <button onClick={()=>accept(true)} style={{ padding:'8px 18px', borderRadius:8, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontSize:12, fontFamily:font.body, cursor:'pointer', fontWeight:700 }}>
                Accept all
              </button>
            </div>
          </div>
        ) : (
          // Detailed preferences
          <div>
            <p style={{ fontSize:14, fontWeight:700, color:C.white, fontFamily:font.head, marginBottom:16 }}>Cookie preferences</p>
            {[
              { key:'essential', label:'Essential', desc:'Required for the app to function. Cannot be disabled.', locked:true },
              { key:'analytics', label:'Analytics', desc:'Helps us understand how you use Jötr so we can improve it.', locked:false },
              { key:'marketing', label:'Marketing', desc:'Used to show relevant content and measure campaign effectiveness.', locked:false },
            ].map(cat => (
              <div key={cat.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                <div>
                  <p style={{ fontSize:13, fontWeight:600, color:C.white, margin:'0 0 2px', fontFamily:font.body }}>{cat.label}</p>
                  <p style={{ fontSize:11, color:'rgba(255,255,255,0.45)', margin:0, fontFamily:font.body }}>{cat.desc}</p>
                </div>
                <div onClick={()=>{ if(!cat.locked) setPrefs(p => ({ ...p, [cat.key]: !p[cat.key as keyof typeof p] })); }}
                  style={{ width:40, height:22, borderRadius:11, background: (cat.locked || prefs[cat.key as keyof typeof prefs]) ? C.amber300 : 'rgba(255,255,255,0.15)', cursor:cat.locked?'not-allowed':'pointer', position:'relative', transition:'background .2s', flexShrink:0 }}>
                  <div style={{ width:16, height:16, borderRadius:'50%', background:C.white, position:'absolute', top:3, left:(cat.locked || prefs[cat.key as keyof typeof prefs]) ? 21 : 3, transition:'left .2s' }}/>
                </div>
              </div>
            ))}
            <div style={{ display:'flex', gap:8, marginTop:16, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowDetail(false)} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.2)', background:'transparent', color:'rgba(255,255,255,0.7)', fontSize:12, fontFamily:font.body, cursor:'pointer', fontWeight:600 }}>
                Back
              </button>
              <button onClick={()=>accept(false)} style={{ padding:'8px 18px', borderRadius:8, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontSize:12, fontFamily:font.body, cursor:'pointer', fontWeight:700 }}>
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Shared Navbar ────────────────────────────────────────────────────────────
export const PageNav: React.FC = () => (
  <nav style={{ height:64, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 clamp(20px,5vw,60px)', background:C.white, position:'sticky', top:0, zIndex:100 }}>
    <a href="/" style={{ display:'flex', alignItems:'center', gap:10, textDecoration:'none' }}>
      <div style={{ width:32, height:32, borderRadius:10, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <svg width="15" height="15" viewBox="0 0 180 180" fill="none">
          <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
          <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
          <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
        </svg>
      </div>
      <span style={{ fontFamily:font.head, fontWeight:800, fontSize:20, letterSpacing:'-0.05em', color:C.ink }}>Jötr</span>
    </a>
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <a href="/login"  style={{ fontSize:14, fontWeight:600, color:C.muted, textDecoration:'none', padding:'8px 14px', borderRadius:8 }}>Log in</a>
      <a href="/signup" style={{ fontSize:14, fontWeight:700, color:C.white, textDecoration:'none', padding:'8px 18px', borderRadius:9, background:`linear-gradient(135deg,${C.amber300},${C.amber400})` }}>Get started</a>
    </div>
  </nav>
);

// ─── Shared Footer ────────────────────────────────────────────────────────────
export const PageFooter: React.FC = () => (
  <footer style={{ background:C.dark, padding:'48px clamp(20px,5vw,60px) 32px', marginTop:'auto' }}>
    <div style={{ maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'32px 24px', marginBottom:40 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="13" height="13" viewBox="0 0 180 180" fill="none">
                <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
                <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
                <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
              </svg>
            </div>
            <span style={{ fontFamily:font.head, fontWeight:800, fontSize:17, letterSpacing:'-0.05em', color:C.white }}>Jötr</span>
          </div>
          <p style={{ fontSize:12, color:'rgba(255,255,255,0.4)', fontFamily:font.body, lineHeight:1.7, maxWidth:180 }}>Your notes, findable. Free forever.</p>
        </div>
        {[
          { h:'Product',  links:[['Features','/#features'],['Pricing','/#pricing'],['Changelog','/changelog'],['Roadmap','/#']] },
          { h:'Company',  links:[['About','/about'],['Blog','/blog'],['Careers','/careers'],['Contact','/contact']] },
          { h:'Legal',    links:[['Privacy Policy','/privacy'],['Terms of Service','/terms'],['Cookie Policy','/cookies']] },
          { h:'Support',  links:[['Help Center','/#'],['Status','/#'],['Security','/#']] },
        ].map(col => (
          <div key={col.h}>
            <p style={{ fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.3)', textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:font.body, marginBottom:14 }}>{col.h}</p>
            {col.links.map(([label, href]) => (
              <a key={label} href={href} style={{ display:'block', fontSize:13, color:'rgba(255,255,255,0.55)', textDecoration:'none', marginBottom:10, fontFamily:font.body, transition:'color .15s' }}
                onMouseEnter={e=>(e.currentTarget.style.color=C.amber300)}
                onMouseLeave={e=>(e.currentTarget.style.color='rgba(255,255,255,0.55)')}>
                {label}
              </a>
            ))}
          </div>
        ))}
      </div>
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.08)', paddingTop:24, display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'center', gap:12 }}>
        <p style={{ fontSize:12, color:'rgba(255,255,255,0.3)', fontFamily:font.body, margin:0 }}>
          © {new Date().getFullYear()} Jötr. All rights reserved.
        </p>
        <div style={{ display:'flex', gap:16 }}>
          {[['Privacy','/privacy'],['Terms','/terms'],['Cookies','/cookies']].map(([l,h]) => (
            <a key={l} href={h} style={{ fontSize:12, color:'rgba(255,255,255,0.3)', textDecoration:'none', fontFamily:font.body }}
              onMouseEnter={e=>(e.currentTarget.style.color=C.amber300)}
              onMouseLeave={e=>(e.currentTarget.style.color='rgba(255,255,255,0.3)')}>
              {l}
            </a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);

// ─── Page wrapper ─────────────────────────────────────────────────────────────
interface LegalLayoutProps {
  title: string;
  subtitle?: string;
  lastUpdated?: string;
  children: React.ReactNode;
}

const LegalLayout: React.FC<LegalLayoutProps> = ({ title, subtitle, lastUpdated, children }) => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      html { scroll-behavior: smooth; }
      body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; color: #1A1814; min-height: 100vh; display: flex; flex-direction: column; }
      #root { display: flex; flex-direction: column; min-height: 100vh; }
      a { color: #C4811A; }
      a:hover { color: #F5A623; }
      h2 { font-family: 'Sora', sans-serif; font-weight: 700; font-size: clamp(17px, 2vw, 20px); color: #1A1814; margin: 36px 0 12px; letter-spacing: -0.02em; }
      h3 { font-family: 'Sora', sans-serif; font-weight: 700; font-size: clamp(14px, 1.5vw, 16px); color: #1A1814; margin: 24px 0 8px; }
      p  { font-size: 15px; line-height: 1.8; color: #3D3A34; margin-bottom: 14px; }
      ul, ol { padding-left: 22px; margin-bottom: 14px; }
      li { font-size: 15px; line-height: 1.8; color: #3D3A34; margin-bottom: 6px; }
      strong { color: #1A1814; font-weight: 600; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 14px; }
      th { background: #FFF4E0; color: #1A1814; font-weight: 700; padding: 10px 14px; text-align: left; border: 1px solid #E8E4DC; font-family: 'DM Sans', sans-serif; }
      td { padding: 10px 14px; border: 1px solid #E8E4DC; color: #3D3A34; vertical-align: top; }
      tr:nth-child(even) td { background: #FAFAF8; }
      @media (max-width: 640px) { table { font-size: 12px; } th, td { padding: 8px 10px; } }
    `}</style>
    <PageNav/>
    <main style={{ flex: 1 }}>
      {/* Hero */}
      <div style={{ background:`linear-gradient(135deg,#FFF4E0,#FFFDF8)`, borderBottom:`1px solid #E8E4DC`, padding:'52px clamp(20px,5vw,60px) 44px' }}>
        <div style={{ maxWidth:760, margin:'0 auto' }}>
          {lastUpdated && <p style={{ fontSize:12, fontWeight:600, color:'#C4811A', marginBottom:10, textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Sans',sans-serif" }}>Last updated: {lastUpdated}</p>}
          <h1 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:'clamp(28px,5vw,44px)', letterSpacing:'-0.04em', color:'#1A1814', marginBottom:12, lineHeight:1.1 }}>{title}</h1>
          {subtitle && <p style={{ fontSize:'clamp(15px,2vw,17px)', color:'#7A7570', lineHeight:1.7, maxWidth:560, margin:0 }}>{subtitle}</p>}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:760, margin:'0 auto', padding:'48px clamp(20px,5vw,60px) 80px' }}>
        {children}
      </div>
    </main>
    <PageFooter/>
    <CookieBanner/>
  </>
);

export default LegalLayout;
